import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getTeamById } from '../api/league';
import { getUniverses } from '../redux/actions/SignUp';
import { switchActiveClub } from '../redux/actions/User';
import { getLoadingUniversesData } from '../redux/selector/SignUp';
import { dataForSidebar } from '../redux/selector/Sidebar';
import { getUser } from '../redux/selector/User';
import {
  clubLabel,
  leagueRefFromTeam,
  leagueRefFromUser,
  resolveUniverseName,
} from '../service/clubServer';

const JOIN_SECOND_LABEL = '+ Join a second team';

const leagueFromTeamPayload = (payload) =>
  payload?.team?.league ||
  payload?.league ||
  payload?.team?.league_id ||
  payload?.league_id ||
  null;

const crestFromTeam = (team, user) =>
  team?.symbol ||
  team?.logo ||
  user?.symbol ||
  user?.team_info?.symbol ||
  null;

const crestFromPayload = (payload) =>
  payload?.team?.symbol ||
  payload?.symbol ||
  payload?.logo ||
  null;

const useClubSwitchOptions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const data = useSelector(dataForSidebar);
  const { leagueName, team_info: teamInfo } = useSelector(getUser);
  const { universesList } = useSelector(getLoadingUniversesData);
  const [fetchedSecond, setFetchedSecond] = useState(null);

  useEffect(() => {
    if (!universesList?.length) {
      dispatch(getUniverses(false));
    }
  }, [dispatch, universesList?.length]);

  const currentTeam = data.user_team || teamInfo;
  const secondUser = data.second_user;
  const secondTeamRaw = secondUser?.team;
  const secondTeamId = secondTeamRaw?._id;
  const secondCrestFromPayload = crestFromTeam(secondTeamRaw, secondUser);

  const currentServer = useMemo(
    () =>
      resolveUniverseName(leagueRefFromTeam(currentTeam), universesList) ||
      resolveUniverseName(leagueName, universesList),
    [currentTeam, leagueName, universesList]
  );

  const secondServerFromPayload = useMemo(
    () =>
      resolveUniverseName(leagueRefFromTeam(secondTeamRaw), universesList) ||
      resolveUniverseName(leagueRefFromUser(secondUser), universesList),
    [secondTeamRaw, secondUser, universesList]
  );

  useEffect(() => {
    if (!secondTeamId) {
      setFetchedSecond(null);
      return undefined;
    }
    if (secondCrestFromPayload && secondServerFromPayload) {
      setFetchedSecond(null);
      return undefined;
    }

    let cancelled = false;
    setFetchedSecond(null);
    const loadSecondTeam = async () => {
      try {
        const response = await getTeamById(secondTeamId);
        if (response.status !== 200) return;
        const payload = await response.json();
        if (cancelled) return;
        setFetchedSecond({
          league: leagueFromTeamPayload(payload),
          symbol: crestFromPayload(payload),
          name: payload?.team?.name || payload?.name || null,
        });
      } catch (error) {
        if (!cancelled) setFetchedSecond(null);
      }
    };

    loadSecondTeam();
    return () => {
      cancelled = true;
    };
  }, [secondTeamId, secondCrestFromPayload, secondServerFromPayload]);

  const secondTeam = useMemo(() => {
    if (!secondTeamRaw) return null;
    return {
      ...secondTeamRaw,
      name: secondTeamRaw.name || fetchedSecond?.name,
      symbol: secondCrestFromPayload || fetchedSecond?.symbol,
    };
  }, [secondTeamRaw, secondCrestFromPayload, fetchedSecond]);

  const secondServer = useMemo(
    () =>
      secondServerFromPayload ||
      resolveUniverseName(fetchedSecond?.league, universesList),
    [secondServerFromPayload, fetchedSecond, universesList]
  );

  const currentLabel = clubLabel(currentTeam?.name, currentServer);
  const secondLabel = secondTeam
    ? clubLabel(secondTeam.name, secondServer)
    : JOIN_SECOND_LABEL;

  const switchClub = async (choice) => {
    if (choice === 'join' && !secondTeam) {
      history.push('/signup', { forSecondTeam: 'true' });
      return;
    }
    // The API switches to the account's other club. Display names (and
    // incomplete team IDs in sidebar responses) must not decide this action.
    if (choice !== 'other' || !secondTeam) return;
    await dispatch(switchActiveClub(history));
  };

  return {
    currentTeam,
    secondTeam,
    currentLabel,
    secondLabel,
    currentServer,
    secondServer,
    switchClub,
  };
};

export default useClubSwitchOptions;
